import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { Button } from "@/design-system/components/react";
import {
  contentError,
  getContent,
  permissionLabel,
  type ContentDetail,
  type ClinicalCase,
  type ContentAsset,
  formatLabel,
  deviceLabel,
  type ContentCandidate,
  type DeviceId,
} from "@/services/contentLibrary";
import { PermissionForm } from "@/components/contentLibrary/PermissionForm";

function CopyText({ label, text }: { label: string; text: string }) {
  const [message, setMessage] = useState("");
  return (
    <details className="border-t border-border-strong pt-2">
      <summary className="cursor-pointer text-sm font-medium">{label}</summary>
      <pre className="my-2 whitespace-pre-wrap break-all rounded bg-bg p-3 text-sm">{text}</pre>
      <Button
        variant="outline"
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(text);
            setMessage("Copied");
          } catch {
            setMessage("Copy failed. Select the text to copy it.");
          }
        }}
      >
        Copy
      </Button>
      <span role="status" className="ml-2 text-xs">
        {message}
      </span>
    </details>
  );
}

function MediaPreview({ asset, label }: { asset: ContentAsset; label: string }) {
  return asset.content_type === "video/mp4" ? (
    <video controls src={asset.url} className="w-full rounded" aria-label={label} />
  ) : (
    <img
      src={asset.url}
      alt={label}
      className="aspect-square w-full rounded object-contain bg-black"
    />
  );
}

function CaseMedia({ record }: { record: ContentDetail }) {
  const { candidate, media } = record;
  return (
    <div className="space-y-3">
      {candidate.cases.length ? (
        <div className={candidate.kind === "flag_set" ? "grid gap-3 sm:grid-cols-2" : "space-y-3"}>
          {candidate.cases.map((item, index) => (
            <div key={`${item.study_uid}:${index}`} className="space-y-2">
              <h3 className="text-sm font-medium">Case {index + 1}</h3>
              {item.media_paths.map((path) => {
                const asset = media.find((entry) => entry.path === path);
                return (
                  asset && (
                    <MediaPreview key={path} asset={asset} label={`Case ${index + 1} preview`} />
                  )
                );
              })}
            </div>
          ))}
        </div>
      ) : (
        media.map((asset, index) => (
          <div key={asset.path} className="space-y-2">
            <p className="text-xs text-fg-muted">
              Slide {index + 1} of {media.length}
            </p>
            <MediaPreview asset={asset} label={`${candidate.title}, slide ${index + 1}`} />
          </div>
        ))
      )}
      <p className="text-xs text-fg-muted">Candidate preview. Not for diagnostic use.</p>
    </div>
  );
}

const splitDevices: [DeviceId, string][] = [
  ["a2z-unified-triage", "Unified"],
  ["a2z-abdo-triage", "Abdo"],
  ["a2z-drafter", "Drafter"],
];

function flagSplit(cases: ClinicalCase[]) {
  const calls = (device: DeviceId) =>
    cases.flatMap((item) => item.calls.filter((call) => call.device === device));
  return splitDevices
    .filter(([device]) => calls(device).length)
    .map(
      ([device, name]) =>
        `${name} flags ${calls(device).filter((call) => call.flagged.length).length} of ${cases.length}`,
    )
    .join(" · ");
}

function FlagSetCalls({ candidate }: { candidate: ContentCandidate }) {
  return (
    <section className="space-y-3" aria-label="Flag set calls">
      {candidate.review_banner && (
        <p role="note" className="rounded border border-border-strong p-3 font-medium">
          {candidate.review_banner}
        </p>
      )}
      <p className="text-sm text-fg-muted">{flagSplit(candidate.cases)}</p>
      <p className="text-xs text-fg-muted">
        Not flagged does not mean normal: every study is still read in full.
      </p>
      <ul className="space-y-2">
        {candidate.cases.map((item, index) => (
          <li key={`${item.study_uid}:${index}`} className="space-y-1">
            <h3 className="text-sm font-medium">Case {index + 1}</h3>
            <p className="text-xs text-fg-muted">
              {`Image: ${deviceLabel[item.device]} key slice for ${item.finding}`}
            </p>
            <div className="flex flex-wrap gap-2">
              {item.calls.map((call) => (
                <span
                  key={call.device}
                  className="rounded border border-border-strong px-2 py-1 text-xs"
                >
                  {deviceLabel[call.device]}:{" "}
                  {call.flagged.length ? `Flagged - ${call.flagged.join(", ")}` : "Not flagged"}
                </span>
              ))}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

function PermissionHistory({ record }: { record: ContentDetail }) {
  return (
    <details className="border-t border-border-strong pt-3">
      <summary className="cursor-pointer text-sm font-medium">
        Permission history ({record.history.length})
      </summary>
      <ol className="mt-3 space-y-3 text-sm">
        {record.history.map((event) => (
          <li key={event.revision}>
            <p>
              Revision {event.revision}: {event.permission.status}
            </p>
            <p className="text-fg-muted">
              {event.actor} · {new Date(event.recorded_at).toLocaleString()}
            </p>
            {event.permission.evidence_url && (
              <a
                href={event.permission.evidence_url}
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
              >
                Evidence
              </a>
            )}
            <p className="break-all">
              Covered series: {event.permission.approved_series.join(", ") || "None"}
            </p>
            <p className="break-all">
              Excluded series: {event.permission.excluded_series.join(", ") || "None"}
            </p>
            <p className="whitespace-pre-wrap">{event.permission.notes}</p>
          </li>
        ))}
      </ol>
    </details>
  );
}

function CaseSources({ item, index }: { item: ClinicalCase; index: number }) {
  const caseId = `${item.study_uid}::reg-${item.run_id}-${item.study_uid}`;
  const viewer = `/case-viewer/${encodeURIComponent(caseId)}?cohort=${encodeURIComponent(item.run_id)}`;
  return (
    <details className="rounded border border-border-strong p-3">
      <summary className="cursor-pointer text-sm font-medium">
        Case {index + 1}: {item.finding}
      </summary>
      <div className="mt-3 space-y-3">
        <p className="text-xs text-fg-muted">{item.device}</p>
        <Link to={viewer} className="text-sm underline">
          Inspect study {index + 1}
        </Link>
        <p className="text-sm text-fg-muted">Report excerpt: {item.report_excerpt}</p>
        <CopyText
          label="Source DICOM locations - internal"
          text={item.source_dicom_uris.join("\n")}
        />
        <CopyText label="Selected series" text={item.series_uids.join("\n")} />
      </div>
    </details>
  );
}

export function ContentCase({ id, userId }: { id: string; userId: string }) {
  const query = useQuery({
    queryKey: ["content-library", userId, "case", id],
    queryFn: () => getContent(id),
    refetchOnWindowFocus: false,
  });
  if (query.isPending) return <p role="status">Loading candidate…</p>;
  if (query.isError) return <p role="alert">{contentError(query.error)}</p>;
  const record = query.data;
  const candidate = record.candidate;
  return (
    <article className="space-y-5" aria-label="Selected candidate">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold">{candidate.title}</h2>
          <p className="text-sm text-fg-muted">
            {formatLabel[candidate.kind]} · {permissionLabel(record)}
          </p>
        </div>
        <Button variant="outline" onClick={() => query.refetch()}>
          Refresh content
        </Button>
      </header>
      {candidate.hold_notice && (
        <p role="note" className="rounded border border-border-strong p-3 font-medium">
          {candidate.hold_notice}
        </p>
      )}
      <div className={candidate.cases.length ? "grid gap-6 xl:grid-cols-2" : "mx-auto max-w-3xl"}>
        <div className="min-w-0 space-y-3">
          {candidate.kind === "flag_set" && <FlagSetCalls candidate={candidate} />}
          {!!record.media.length && <CaseMedia record={record} />}
          {candidate.kind === "poll" && (
            <div className="rounded border border-border-strong p-4">
              <h3 className="font-medium">Poll options</h3>
              <ol className="mt-3 list-decimal space-y-2 pl-5">
                {candidate.options.map((option, index) => (
                  <li key={index}>{option}</li>
                ))}
              </ol>
              <CopyText
                label="Question and options"
                text={[candidate.title, ...candidate.options].join("\n")}
              />
            </div>
          )}
          {candidate.cases.map((item, index) => (
            <CaseSources key={`${item.study_uid}:${index}`} item={item} index={index} />
          ))}
          {candidate.post_order.map((label) => (
            <CopyText
              key={label}
              label={label}
              text={
                candidate.hold_notice
                  ? `${candidate.hold_notice}\n\n${candidate.posts[label]}`
                  : candidate.posts[label]
              }
            />
          ))}
        </div>
        {!!candidate.cases.length && (
          <div className="min-w-0 space-y-5">
            {record.can_record_permission === true ? (
              <PermissionForm key={`${id}:${record.revision}`} record={record} userId={userId} />
            ) : (
              <div className="space-y-2">
                <h3 className="text-base font-semibold">Supplier permission - read-only</h3>
                <p className="text-sm text-fg-muted">
                  Record supplier permission in production. The clearance history below is shared.
                </p>
              </div>
            )}
            <PermissionHistory record={record} />
          </div>
        )}
      </div>
    </article>
  );
}
